/**
 * useParseErrorMarker — emits a Monaco error squiggle for the cursor statement
 * when it genuinely fails to parse (VQE Polish layer 1, EDG-12/14/15: "no
 * squiggle at all"). Uses `SqlMonaco`'s existing imperative `setMarkers`
 * handle under a DEDICATED owner (`'vqe-parse'`) so it never collides with the
 * IntelliSense diagnostic owners (`'sql'` / `'sql-syntax'` / `'sql-typecheck'`
 * / `'package-errors'`). It touches NO global editor option — the squiggle is
 * additive, produced only through the marker handle.
 *
 * Lifecycle: the marker is emitted only while `unavailableReason` is
 * `'parse-error'` AND a statement range is known; every other state — including
 * the valid-but-unavailable reasons (`'procedural'` / `'set-op-precedence'`,
 * which are perfectly valid SQL and must NOT be flagged as errors) — clears the
 * owner so no stale squiggle survives. The effect also clears on unmount and
 * whenever the reason moves away from `'parse-error'` or the cursor leaves the
 * statement (range → null).
 *
 * Kept out of the shared `SqlMonaco` component (which stays a dumb marker sink)
 * so the parse-error lifecycle is unit-testable against a stub handle and lives
 * beside the affordance that computes the reason.
 */
import { useEffect } from 'react';

import type { EditorSelectionRange } from '../../../../types/aiWorkspace';
import type { SqlMonacoHandle, SqlMonacoMarker } from '../../common/utils/sqlMonacoTypes';
import { vqeCopy } from '../ux/vqeCopy';
import type { VqeUnavailableReason } from './vqeEditAffordance';

/** Dedicated Monaco marker owner for the VQE parse-error squiggle. */
export const VQE_PARSE_MARKER_OWNER = 'vqe-parse';

export function useParseErrorMarker(
  editorRef: React.RefObject<SqlMonacoHandle | null>,
  unavailableReason: VqeUnavailableReason,
  statementRange: EditorSelectionRange | null,
): void {
  const start = statementRange?.startLineNumber ?? null;
  const startCol = statementRange?.startColumn ?? null;
  const end = statementRange?.endLineNumber ?? null;
  const endCol = statementRange?.endColumn ?? null;
  const isParseError = unavailableReason === 'parse-error';

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    if (isParseError && start !== null && startCol !== null && end !== null && endCol !== null) {
      const marker: SqlMonacoMarker = {
        startLineNumber: start,
        startColumn: startCol,
        endLineNumber: end,
        endColumn: endCol,
        message: vqeCopy.unavailable.parseError(),
        severity: 'error',
      };
      editor.setMarkers([marker], VQE_PARSE_MARKER_OWNER);
    } else {
      editor.setMarkers([], VQE_PARSE_MARKER_OWNER);
    }

    // Always clear on teardown so a moved cursor / unmount never leaves a stale
    // red squiggle on a statement that has since changed or parsed. Clear on the
    // SAME handle we set the marker through (captured above) — that is exactly
    // the owner-scoped marker we own.
    return () => {
      editor.setMarkers([], VQE_PARSE_MARKER_OWNER);
    };
  }, [editorRef, isParseError, start, startCol, end, endCol]);
}
